import React, { useState, useEffect } from 'react';
import { MessageSquare, X, ChevronLeft, Sparkles } from 'lucide-react';
import ChatAdminSelector from './ChatAdminSelector';
import ChatWindow from './ChatWindow';
import { chatApi } from '../../api/chatApi';
import { useAuth } from '../../hooks/useAuth';
import { useSocket } from '../../hooks/useSocket';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const { user } = useAuth();
  const { socket } = useSocket();

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const res = await chatApi.getChatAdmins();
      setAdmins(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchAdmins();
    }
  }, [isOpen]);

  const filteredAdmins = admins.filter((a) => {
    const term = search.toLowerCase();
    return (
      a.name.toLowerCase().includes(term) ||
      (a.company && a.company.name.toLowerCase().includes(term))
    );
  });

  const totalUnread = admins.reduce((sum, a) => sum + (a.unreadCount || 0), 0);

  return (
    <>
      {/* Floating Chat Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 p-4 rounded-2xl bg-gradient-to-tr from-emerald-600 via-emerald-500 to-teal-400 text-white shadow-lg shadow-emerald-600/40 hover:shadow-emerald-500/60 hover:scale-105 active:scale-95 transition-all duration-200 group flex items-center justify-center ring-4 ring-emerald-500/10"
      >
        <MessageSquare className="w-6 h-6 group-hover:rotate-12 transition-transform" />
        {totalUnread > 0 ? (
          <span className="absolute -top-2 -right-2 px-2 py-0.5 rounded-full bg-rose-500 text-[10px] font-bold text-white shadow-md animate-bounce ring-2 ring-white">
            {totalUnread}
          </span>
        ) : (
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-white animate-pulse" />
        )}
      </button>

      {/* Floating Drawer Modal */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-50 w-[92vw] sm:w-[560px] md:w-[720px] h-[580px] bg-white/95 backdrop-blur-xl border border-slate-200/90 rounded-3xl shadow-[0_25px_60px_-15px_rgba(0,0,0,0.15)] overflow-hidden flex flex-col animate-fadeIn">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3.5 bg-white/90 border-b border-slate-100">
            <div className="flex items-center space-x-2.5">
              {selectedAdmin && (
                <button
                  onClick={() => setSelectedAdmin(null)}
                  className="p-1 rounded-lg text-slate-400 hover:text-slate-900 sm:hidden mr-1"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              )}
              <div className="p-2 bg-emerald-50 rounded-xl border border-emerald-100 text-emerald-700">
                <MessageSquare className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-sm font-extrabold text-slate-900 font-display flex items-center gap-1.5">
                  SuperAdmin Chat Hub
                  <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[9px] font-extrabold uppercase">Socket Live</span>
                </h3>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1.5 rounded-xl text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Split Pane View */}
          <div className="flex-1 flex overflow-hidden">
            <div
              className={`w-full sm:w-64 flex-shrink-0 ${
                selectedAdmin ? 'hidden sm:block' : 'block'
              }`}
            >
              <ChatAdminSelector
                admins={filteredAdmins}
                selectedAdmin={selectedAdmin}
                onSelectAdmin={(admin) => setSelectedAdmin(admin)}
                loading={loading}
                search={search}
                onSearchChange={setSearch}
              />
            </div>

            <div className={`flex-1 ${!selectedAdmin ? 'hidden sm:flex' : 'flex'}`}>
              <ChatWindow
                selectedAdmin={selectedAdmin}
                currentUserId={user?._id}
                socket={socket}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;

